import React, { useEffect, useState } from 'react';
import { Button, Card, Input, InputNumber, message, Modal, Popconfirm, Select, Space, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import api from '../api/client';
import { useStores } from '../hooks/useStores';
import { useDebounced } from '../hooks/useDebounced';
import { fetchShipStationWarehouses } from '../api/products.api';

interface SummaryRow {
  warehouseId: string;
  masterSku: string;
  attempts: number;
  succeeded: number;
  failed: number;
  ignored: number;
  netQty: number;
  lastAttempt: string;
}

interface FailedRow {
  id: number;
  orderId: number | null;
  order_number: string | null;
  tracking_number: string | null;
  warehouse_id: string;
  original_sku: string;
  master_sku: string | null;
  qty_delta: number;
  http_status: number | null;
  ss_error_body: unknown;
  attempted_at: string;
}

interface AliasRow {
  alias_sku: string;
  store_id: number;
  master_sku: string;
  product_id: number | null;
  source: 'auto' | 'manual';
  updated_by: string | null;
  resolved_at: string;
}

interface CandidateProduct {
  productId: number | null;
  sku: string;
  name: string | null;
  aliases: string[];
}

function StatCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ padding: 12, background: '#fafafa', border: '1px solid #eee', borderRadius: 4 }}>
      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 16, marginTop: 4 }}>{value}</div>
    </div>
  );
}

interface CatalogStatus {
  running: boolean;
  productCount: number;
  aliasCount: number;
  lastSyncStartedAt: string | null;
  lastSyncEndedAt: string | null;
  lastFullSyncAt: string | null;
  lastModifyCursor: string | null;
  productsUpserted: number | null;
  aliasesUpserted: number | null;
  lastError: string | null;
}

interface HistoryRow {
  id: number;
  alias_sku: string;
  store_id: number;
  prev_master: string | null;
  new_master: string;
  prev_source: 'auto' | 'manual' | null;
  new_source: 'auto' | 'manual';
  changed_by: string | null;
  changed_at: string;
}

interface SnapshotResult {
  warehouseId: string;
  masterSku: string;
  ssOnHand: number | null;
  ssAvailable: number | null;
  expected: number | null;
  delta: number | null;
  snapshotAt: string | null;
  lastSnapshotAt: string | null;
  ssError?: string;
}

export default function InventoryReconciliation() {
  const [days, setDays] = useState(7);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounced(searchInput, 300);
  const [summary, setSummary] = useState<SummaryRow[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [failedByKey, setFailedByKey] = useState<Record<string, FailedRow[]>>({});
  const [actioningKey, setActioningKey] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<Record<string, SnapshotResult>>({});
  const [snapshotLoadingKey, setSnapshotLoadingKey] = useState<string | null>(null);
  const [aliases, setAliases] = useState<AliasRow[]>([]);
  const [aliasLoading, setAliasLoading] = useState(false);
  const [refreshingAliases, setRefreshingAliases] = useState(false);
  const [warehouseName, setWarehouseName] = useState<Record<string, string>>({});
  useEffect(() => {
    fetchShipStationWarehouses()
      .then((wh: any) => {
        const map: Record<string, string> = {};
        for (const w of wh?.inventory_warehouses ?? wh ?? []) {
          const id = String(w.inventory_warehouse_id ?? w.id ?? '');
          if (id) map[id] = w.name ?? id;
        }
        setWarehouseName(map);
      })
      .catch(() => { /* non-critical */ });
  }, []);
  const warehouseLabel = (id: string): string =>
    warehouseName[id] ? `${id} (${warehouseName[id]})` : id;

  // Override modal state
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideAlias, setOverrideAlias] = useState('');
  const [overrideStoreId, setOverrideStoreId] = useState<number>(0);
  const [overridePriorMaster, setOverridePriorMaster] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<CandidateProduct[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateProduct | null>(null);
  const [savingOverride, setSavingOverride] = useState(false);

  const stores = useStores();
  const storeNameById = new Map<number, string>(
    (stores ?? []).map((s: any) => [Number(s.storeId), s.storeName as string]),
  );
  const storeOptions = (stores ?? []).map((s: any) => ({
    label: `${s.storeName} (${s.storeId})`,
    value: Number(s.storeId),
  }));

  // History modal state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyAlias, setHistoryAlias] = useState<string | null>(null);
  const [historyRows, setHistoryRows] = useState<HistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Catalog sync state
  const [catalogStatus, setCatalogStatus] = useState<CatalogStatus | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [syncTriggering, setSyncTriggering] = useState(false);

  const keyFor = (warehouseId: string, sku: string) => `${warehouseId}||${sku}`;

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const qs = new URLSearchParams({ days: String(days) });
      const s = debouncedSearch.trim();
      if (s) qs.set('search', s);
      const res = await api.get<{ rows: SummaryRow[] }>(`/shipstation-inventory/recon/summary?${qs.toString()}`);
      setSummary(res.data.rows);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadFailed = async (warehouseId: string, sku: string) => {
    const k = keyFor(warehouseId, sku);
    try {
      const res = await api.get<{ rows: FailedRow[] }>(
        `/shipstation-inventory/recon/failed?warehouseId=${encodeURIComponent(warehouseId)}&sku=${encodeURIComponent(sku)}`,
      );
      setFailedByKey((prev) => ({ ...prev, [k]: res.data.rows }));
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load failed rows');
    }
  };

  const retry = async (warehouseId: string, sku: string) => {
    const k = keyFor(warehouseId, sku);
    setActioningKey(k);
    try {
      const res = await api.post<{ ok: boolean; retried: number; adjusted: boolean; body?: unknown }>(
        '/shipstation-inventory/recon/retry',
        { warehouseId, sku },
      );
      if (res.data.ok) {
        message.success(`Retry succeeded — resolved ${res.data.retried} row(s)`);
      } else {
        message.error(`Retry failed — SS: ${JSON.stringify(res.data.body ?? '')}`);
      }
      await loadSummary();
      await loadFailed(warehouseId, sku);
    } catch (err: any) {
      const body = err.response?.data;
      message.error(`Retry failed — ${body?.body ? JSON.stringify(body.body) : body?.error ?? err.message}`);
    } finally {
      setActioningKey(null);
    }
  };

  const ignore = async (warehouseId: string, sku: string) => {
    const k = keyFor(warehouseId, sku);
    setActioningKey(k);
    try {
      const res = await api.post<{ ignored: number }>('/shipstation-inventory/recon/ignore', {
        warehouseId,
        sku,
      });
      message.success(`Marked ${res.data.ignored} row(s) as ignored`);
      await loadSummary();
      setFailedByKey((prev) => ({ ...prev, [k]: [] }));
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Ignore failed');
    } finally {
      setActioningKey(null);
    }
  };

  const loadSnapshot = async (warehouseId: string, sku: string) => {
    const k = keyFor(warehouseId, sku);
    setSnapshotLoadingKey(k);
    try {
      const res = await api.get<SnapshotResult>(
        `/shipstation-inventory/recon/snapshot?warehouseId=${encodeURIComponent(warehouseId)}&sku=${encodeURIComponent(sku)}`,
      );
      setSnapshots((prev) => ({ ...prev, [k]: res.data }));
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Snapshot fetch failed');
    } finally {
      setSnapshotLoadingKey(null);
    }
  };

  const loadAliases = async () => {
    setAliasLoading(true);
    try {
      const res = await api.get<{ rows: AliasRow[] }>('/shipstation-inventory/recon/aliases');
      setAliases(res.data.rows);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load aliases');
    } finally {
      setAliasLoading(false);
    }
  };

  const deleteAliasRow = async (aliasSku: string, storeId: number) => {
    try {
      const res = await api.post<{ deleted: number; previous_master?: string | null }>(
        '/shipstation-inventory/recon/aliases/delete',
        { alias_sku: aliasSku, store_id: storeId },
      );
      const scope = storeId > 0 ? (storeNameById.get(storeId) ?? `store ${storeId}`) : 'global';
      message.success(
        res.data.deleted > 0
          ? `Deleted ${aliasSku} → ${res.data.previous_master} (${scope}). History preserved.`
          : 'Nothing to delete.',
      );
      await loadAliases();
      await loadSummary();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const clearGlobal = async () => {
    try {
      const res = await api.post<{ deleted: number }>('/shipstation-inventory/recon/aliases/clear-global');
      message.success(`Cleared ${res.data.deleted} global alias mapping(s). Store-specific rows untouched.`);
      await loadAliases();
      await loadSummary();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Clear failed');
    }
  };

  const refreshAliases = async () => {
    setRefreshingAliases(true);
    try {
      const res = await api.post<{ cleared: number }>('/shipstation-inventory/recon/aliases/refresh');
      message.success(`Cleared ${res.data.cleared} alias mappings — will re-resolve on next adjust`);
      await loadAliases();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Refresh failed');
    } finally {
      setRefreshingAliases(false);
    }
  };

  const openOverride = async (aliasSku: string, storeId: number, priorMaster: string | null) => {
    setOverrideAlias(aliasSku);
    setOverrideStoreId(storeId);
    setOverridePriorMaster(priorMaster);
    setCandidates([]);
    setSelectedCandidate(null);
    setOverrideOpen(true);
    if (aliasSku) await fetchCandidates(aliasSku, storeId);
  };

  const fetchCandidates = async (aliasSku: string, storeId: number) => {
    setCandidatesLoading(true);
    try {
      const qs = new URLSearchParams({ sku: aliasSku });
      if (storeId > 0) qs.set('storeId', String(storeId));
      const res = await api.get<{ candidates: CandidateProduct[] }>(
        `/shipstation-inventory/recon/aliases/candidates?${qs.toString()}`,
      );
      setCandidates(res.data.candidates);
      if (res.data.candidates.length === 1) setSelectedCandidate(res.data.candidates[0]);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to fetch candidates');
    } finally {
      setCandidatesLoading(false);
    }
  };

  const saveOverride = async (masterSku: string, productId: number | null) => {
    setSavingOverride(true);
    try {
      const res = await api.post<{ replaced: boolean; previous_master?: string | null }>(
        '/shipstation-inventory/recon/aliases/override',
        {
          alias_sku: overrideAlias,
          store_id: overrideStoreId,
          master_sku: masterSku,
          product_id: productId,
        },
      );
      const scope = overrideStoreId > 0 ? `store ${storeNameById.get(overrideStoreId) ?? overrideStoreId}` : 'global';
      if (res.data.replaced) {
        message.success(
          `Override set: ${overrideAlias} → ${masterSku} for ${scope} (was → ${res.data.previous_master}). Prior mapping preserved in history.`,
        );
      } else {
        message.success(`Override set: ${overrideAlias} → ${masterSku} for ${scope}`);
      }
      setOverrideOpen(false);
      await loadAliases();
      await loadSummary();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save override');
    } finally {
      setSavingOverride(false);
    }
  };

  const openHistory = async (aliasSku?: string) => {
    setHistoryAlias(aliasSku ?? null);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const qs = aliasSku ? `?alias=${encodeURIComponent(aliasSku)}` : '';
      const res = await api.get<{ rows: HistoryRow[] }>(`/shipstation-inventory/recon/aliases/history${qs}`);
      setHistoryRows(res.data.rows);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadCatalogStatus = async () => {
    setCatalogLoading(true);
    try {
      const res = await api.get<CatalogStatus>('/shipstation-inventory/recon/catalog/status');
      setCatalogStatus(res.data);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load catalog status');
    } finally {
      setCatalogLoading(false);
    }
  };

  const triggerSync = async (full: boolean) => {
    setSyncTriggering(true);
    try {
      await api.post(`/shipstation-inventory/recon/catalog/sync${full ? '?full=true' : ''}`);
      message.success(full ? 'Full sync started — this can take a while.' : 'Incremental sync started.');
      await loadCatalogStatus();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to trigger sync');
    } finally {
      setSyncTriggering(false);
    }
  };

  useEffect(() => {
    loadSummary();
    loadAliases();
    loadCatalogStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload summary when search / days changes (debounced).
  useEffect(() => {
    loadSummary();
  }, [debouncedSearch, days]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll catalog status every 3s while a sync is running so the UI reflects
  // progress without user reload.
  useEffect(() => {
    if (!catalogStatus?.running) return;
    const t = setInterval(loadCatalogStatus, 3000);
    return () => clearInterval(t);
  }, [catalogStatus?.running]);

  const summaryColumns = [
    { title: 'Warehouse', dataIndex: 'warehouseId', key: 'warehouseId', width: 220,
      render: (id: string) => warehouseLabel(id) },
    { title: 'Master SKU', dataIndex: 'masterSku', key: 'masterSku' },
    { title: 'Attempts', dataIndex: 'attempts', key: 'attempts', width: 90 },
    { title: 'Succeeded', dataIndex: 'succeeded', key: 'succeeded', width: 100 },
    {
      title: 'Failed',
      dataIndex: 'failed',
      key: 'failed',
      width: 90,
      render: (v: number) => (v > 0 ? <Tag color="red">{v}</Tag> : v),
    },
    { title: 'Ignored', dataIndex: 'ignored', key: 'ignored', width: 90 },
    { title: 'Net Qty', dataIndex: 'netQty', key: 'netQty', width: 90 },
    {
      title: 'Last Attempt',
      dataIndex: 'lastAttempt',
      key: 'lastAttempt',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 260,
      render: (_: unknown, r: SummaryRow) => {
        const k = keyFor(r.warehouseId, r.masterSku);
        const acting = actioningKey === k;
        const snapshotting = snapshotLoadingKey === k;
        return (
          <Space>
            <Popconfirm
              title={`Retry all ${r.failed} failed adjust(s) for ${r.masterSku}?`}
              onConfirm={() => retry(r.warehouseId, r.masterSku)}
              disabled={r.failed === 0}
            >
              <Button size="small" type="primary" disabled={r.failed === 0} loading={acting}>
                Retry ({r.failed})
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Mark all failures as ignored (no SS call)?"
              onConfirm={() => ignore(r.warehouseId, r.masterSku)}
              disabled={r.failed === 0}
            >
              <Button size="small" disabled={r.failed === 0} loading={acting}>Ignore</Button>
            </Popconfirm>
            <Tooltip title="Fetch current SS on-hand and compare against expected">
              <Button size="small" loading={snapshotting} onClick={() => loadSnapshot(r.warehouseId, r.masterSku)}>
                Snapshot
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const failedColumns = [
    { title: 'Order', dataIndex: 'order_number', width: 140 },
    { title: 'Tracking', dataIndex: 'tracking_number', width: 200 },
    { title: 'Original SKU', dataIndex: 'original_sku', width: 150 },
    { title: 'Qty', dataIndex: 'qty_delta', width: 60 },
    { title: 'HTTP', dataIndex: 'http_status', width: 70 },
    {
      title: 'SS Error',
      dataIndex: 'ss_error_body',
      render: (v: unknown) => (
        <Typography.Text style={{ fontSize: 11 }} code>{JSON.stringify(v)}</Typography.Text>
      ),
    },
    {
      title: 'When',
      dataIndex: 'attempted_at',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Inventory Reconciliation</h2>
        <Space>
          <Input.Search
            placeholder="Search SKU, master SKU, order #, warehouse…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={(v) => setSearchInput(v)}
            allowClear
            style={{ width: 340 }}
          />
          <span>Since:</span>
          <InputNumber min={1} max={90} value={days} onChange={(v) => setDays(Number(v ?? 7))} addonAfter="days" />
          <Button icon={<ReloadOutlined />} loading={summaryLoading} onClick={loadSummary}>
            Reload
          </Button>
        </Space>
      </div>

      <Tabs
        defaultActiveKey="catalog"
        items={[
          {
            key: 'catalog',
            label: 'Catalog',
            children: (
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 300 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>SS Product Catalog Mirror</Typography.Title>
                    <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
                      Local copy of SS Products + Aliases (V2). The shipment resolver reads from here,
                      so inventory decrements work with zero SS API calls per order.
                    </Typography.Paragraph>
                  </div>
                  <Space>
                    <Button loading={catalogLoading} onClick={loadCatalogStatus}>Refresh Status</Button>
                    <Button
                      disabled={catalogStatus?.running}
                      loading={syncTriggering}
                      onClick={() => triggerSync(false)}
                    >
                      Sync Incremental
                    </Button>
                    <Popconfirm
                      title="Full sync pulls the entire catalog (~ minutes to hours depending on size). Run it?"
                      onConfirm={() => triggerSync(true)}
                      disabled={catalogStatus?.running}
                    >
                      <Button type="primary" disabled={catalogStatus?.running}>
                        Full Sync
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginTop: 20 }}>
                  <StatCell label="Products" value={catalogStatus?.productCount ?? '-'} />
                  <StatCell label="Aliases" value={catalogStatus?.aliasCount ?? '-'} />
                  <StatCell
                    label="Status"
                    value={catalogStatus?.running ? <Tag color="processing">running</Tag> : <Tag color="green">idle</Tag>}
                  />
                  <StatCell
                    label="Last Full Sync"
                    value={catalogStatus?.lastFullSyncAt ? new Date(catalogStatus.lastFullSyncAt).toLocaleString() : 'never'}
                  />
                  <StatCell
                    label="Last Sync Ended"
                    value={catalogStatus?.lastSyncEndedAt ? new Date(catalogStatus.lastSyncEndedAt).toLocaleString() : '-'}
                  />
                  <StatCell
                    label="Cursor (incremental)"
                    value={catalogStatus?.lastModifyCursor ? new Date(catalogStatus.lastModifyCursor).toLocaleString() : '-'}
                  />
                  <StatCell label="Last Products Upserted" value={catalogStatus?.productsUpserted ?? '-'} />
                  <StatCell label="Last Aliases Upserted" value={catalogStatus?.aliasesUpserted ?? '-'} />
                </div>

                {catalogStatus?.lastError && (
                  <div style={{ marginTop: 16, padding: 12, background: '#fff2f0', border: '1px solid #ffccc7' }}>
                    <Typography.Text type="danger">Last error:</Typography.Text>{' '}
                    <Typography.Text code>{catalogStatus.lastError}</Typography.Text>
                  </div>
                )}
              </Card>
            ),
          },
          {
            key: 'audit',
            label: 'Audit',
            children: (
              <Card>
                <Table
                  size="small"
                  loading={summaryLoading}
                  rowKey={(r) => keyFor(r.warehouseId, r.masterSku)}
                  dataSource={summary}
                  columns={summaryColumns}
                  pagination={{ pageSize: 50, showSizeChanger: true, hideOnSinglePage: true }}
                  expandable={{
                    onExpand: (expanded, r) => {
                      if (expanded && !failedByKey[keyFor(r.warehouseId, r.masterSku)]) {
                        loadFailed(r.warehouseId, r.masterSku);
                      }
                    },
                    expandedRowRender: (r: SummaryRow) => {
                      const k = keyFor(r.warehouseId, r.masterSku);
                      const rows = failedByKey[k] ?? [];
                      const snap = snapshots[k];
                      return (
                        <div>
                          {snap && (
                            <div style={{ marginBottom: 12, padding: 8, background: '#fafafa', border: '1px solid #eee' }}>
                              <strong>Snapshot:</strong>{' '}
                              SS on-hand: {snap.ssOnHand ?? '—'}
                              {snap.expected != null && (
                                <> · Expected: {snap.expected} · Delta: {snap.delta != null && snap.delta !== 0 ? <Tag color={snap.delta > 0 ? 'orange' : 'red'}>{snap.delta > 0 ? `+${snap.delta}` : snap.delta}</Tag> : <Tag color="green">match</Tag>}</>
                              )}
                              {snap.expected == null && (
                                <> · <Typography.Text type="secondary">First snapshot — baseline recorded; expected value available on next fetch.</Typography.Text></>
                              )}
                              {snap.ssError && <> · <Typography.Text type="danger">{snap.ssError}</Typography.Text></>}
                            </div>
                          )}
                          <Table
                            size="small"
                            rowKey="id"
                            dataSource={rows}
                            columns={failedColumns}
                            pagination={false}
                            locale={{ emptyText: 'No failed rows.' }}
                          />
                        </div>
                      );
                    },
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'aliases',
            label: 'Alias Map',
            children: (
              <Card>
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text type="secondary">
                    SS Inventory tracks stock at the master SKU level. Aliases resolved on first adjust are cached here. Manual overrides win over auto-resolved values.
                  </Typography.Text>
                  <Space>
                    <Button onClick={() => openOverride('', 0, null)}>Add Override</Button>
                    <Button onClick={() => openHistory()}>Change History</Button>
                    <Popconfirm
                      title="Delete every global (store_id=0) alias mapping? Store-specific rows are untouched. History preserved."
                      onConfirm={clearGlobal}
                    >
                      <Button danger>Clear All Global</Button>
                    </Popconfirm>
                    <Popconfirm
                      title="Clear the AUTO alias cache? Manual overrides are preserved. Next auto-resolves will re-fetch from ShipStation."
                      onConfirm={refreshAliases}
                    >
                      <Button loading={refreshingAliases}>Refresh Auto Cache</Button>
                    </Popconfirm>
                  </Space>
                </div>
                <Table
                  size="small"
                  loading={aliasLoading}
                  rowKey="alias_sku"
                  dataSource={aliases}
                  pagination={{ pageSize: 50, showSizeChanger: true, hideOnSinglePage: true }}
                  columns={[
                    { title: 'Alias', dataIndex: 'alias_sku', key: 'alias_sku' },
                    {
                      title: 'Store',
                      dataIndex: 'store_id',
                      key: 'store_id',
                      width: 180,
                      render: (v: number) =>
                        v > 0 ? (storeNameById.get(v) ?? `Store ${v}`) : <Tag>global</Tag>,
                    },
                    { title: 'Master', dataIndex: 'master_sku', key: 'master_sku' },
                    {
                      title: 'Source',
                      dataIndex: 'source',
                      key: 'source',
                      width: 100,
                      render: (v: 'auto' | 'manual', r: AliasRow) =>
                        v === 'manual' ? (
                          <Tooltip title={r.updated_by ? `Set by ${r.updated_by}` : 'Operator override'}>
                            <Tag color="blue">manual</Tag>
                          </Tooltip>
                        ) : (
                          <Tag>auto</Tag>
                        ),
                    },
                    { title: 'SS Product ID', dataIndex: 'product_id', key: 'product_id', width: 140 },
                    {
                      title: 'Resolved',
                      dataIndex: 'resolved_at',
                      key: 'resolved_at',
                      width: 180,
                      render: (v: string) => new Date(v).toLocaleString(),
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      width: 180,
                      render: (_: unknown, r: AliasRow) => (
                        <Space>
                          <Button size="small" onClick={() => openOverride(r.alias_sku, r.store_id, r.master_sku)}>
                            Change
                          </Button>
                          <Button size="small" onClick={() => openHistory(r.alias_sku)}>
                            History
                          </Button>
                          <Popconfirm
                            title={`Delete mapping ${r.alias_sku} → ${r.master_sku}? History preserved.`}
                            onConfirm={() => deleteAliasRow(r.alias_sku, r.store_id)}
                          >
                            <Button size="small" danger>Delete</Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={overrideAlias ? `Set master for ${overrideAlias}` : 'Add Alias Override'}
        open={overrideOpen}
        onCancel={() => setOverrideOpen(false)}
        width={700}
        footer={null}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Space wrap>
            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Alias SKU</div>
              <Input
                value={overrideAlias}
                onChange={(e) => setOverrideAlias(e.target.value)}
                placeholder="e.g. LP-LSL-BC60-MH"
                style={{ width: 260 }}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Store (scope)</div>
              <Select
                value={overrideStoreId}
                onChange={(v) => setOverrideStoreId(Number(v))}
                style={{ width: 260 }}
                options={[{ label: 'Global (all stores)', value: 0 }, ...storeOptions]}
                showSearch
                optionFilterProp="label"
              />
            </div>
            <div style={{ alignSelf: 'flex-end' }}>
              <Button
                type="default"
                loading={candidatesLoading}
                disabled={!overrideAlias.trim()}
                onClick={() => fetchCandidates(overrideAlias.trim(), overrideStoreId)}
              >
                Fetch Candidates
              </Button>
            </div>
          </Space>

          {overridePriorMaster && (
            <div>
              <Typography.Text type="warning">
                Current mapping (for {overrideStoreId > 0 ? storeNameById.get(overrideStoreId) ?? `store ${overrideStoreId}` : 'global'}):{' '}
                <strong>{overrideAlias}</strong> → <strong>{overridePriorMaster}</strong>.
                Saving a new master preserves the prior in history; historical inventory decrements stay attributed to the old master.
              </Typography.Text>
            </div>
          )}

          <Typography.Text strong>SS matches ({candidates.length}):</Typography.Text>
          <Table
            size="small"
            rowKey={(r) => `${r.productId ?? r.sku}`}
            dataSource={candidates}
            loading={candidatesLoading}
            pagination={false}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedCandidate ? [`${selectedCandidate.productId ?? selectedCandidate.sku}`] : [],
              onChange: (_keys, rows) => setSelectedCandidate(rows[0] ?? null),
            }}
            columns={[
              { title: 'Master SKU', dataIndex: 'sku', key: 'sku' },
              { title: 'Product Name', dataIndex: 'name', key: 'name' },
              {
                title: 'Aliases',
                dataIndex: 'aliases',
                key: 'aliases',
                render: (v: string[]) => (v && v.length ? v.map((a) => <Tag key={a}>{a}</Tag>) : '-'),
              },
              { title: 'SS Product ID', dataIndex: 'productId', key: 'productId', width: 140 },
            ]}
            locale={{ emptyText: candidatesLoading ? 'Loading…' : 'Enter an alias and click Fetch Candidates.' }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setOverrideOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              disabled={!selectedCandidate || !overrideAlias}
              loading={savingOverride}
              onClick={() => selectedCandidate && saveOverride(selectedCandidate.sku, selectedCandidate.productId)}
            >
              Save Override
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        title={historyAlias ? `History — ${historyAlias}` : 'Alias Change History'}
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        width={720}
        footer={<Button onClick={() => setHistoryOpen(false)}>Close</Button>}
      >
        <Table
          size="small"
          rowKey="id"
          loading={historyLoading}
          dataSource={historyRows}
          pagination={{ pageSize: 20 }}
          columns={[
            { title: 'Alias', dataIndex: 'alias_sku', key: 'alias_sku' },
            {
              title: 'Store',
              dataIndex: 'store_id',
              key: 'store_id',
              width: 160,
              render: (v: number) => (v > 0 ? storeNameById.get(v) ?? `Store ${v}` : <Tag>global</Tag>),
            },
            {
              title: 'From',
              key: 'from',
              render: (_: unknown, r: HistoryRow) => r.prev_master ? `${r.prev_master} (${r.prev_source})` : '—',
            },
            {
              title: 'To',
              key: 'to',
              render: (_: unknown, r: HistoryRow) => `${r.new_master} (${r.new_source})`,
            },
            { title: 'By', dataIndex: 'changed_by', key: 'changed_by' },
            {
              title: 'When',
              dataIndex: 'changed_at',
              key: 'changed_at',
              render: (v: string) => new Date(v).toLocaleString(),
            },
          ]}
        />
      </Modal>
    </div>
  );
}
